/**
 * @file ShipmentsDeliveryLink.tsx
 * @description Строка-ссылка «Доставка» над списком отгрузок
 * (phase19.24 шаг 5.2, §5).
 *
 * Пришла на смену карточке «Доставка по заказу»: условия доставки живут во
 * вкладке «Доставка», а здесь остаётся физика — отгрузка, трек, события.
 * Строка отвечает на один вопрос кладовщика: чем и куда едет заказ, и где
 * посмотреть условия. Менять их отсюда нельзя — иначе снова появятся два
 * места, где назначается цена.
 *
 * Три состояния (§5) — реестром: согласовано, ещё не согласовано, самовывоз.
 */

import { ArrowRight, MapPin, Truck } from "lucide-react";

import type { OrderDeliveryReceiptDto } from "../../lib/api-delivery-agreement";
import { formatMoney } from "../../lib/api-orders";
import { formatShopDateTime } from "../../lib/shop-time";
import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";

interface Props {
  /** Снапшот условий доставки заказа; `null` — способ ещё не выставлен. */
  receipt: OrderDeliveryReceiptDto | null;
  /** Когда клиент подтвердил условия (ISO); `null` — ещё не подтвердил. */
  confirmedAt: string | null;
  /** Перейти во вкладку «Доставка». */
  onOpenDelivery: () => void;
}

export function ShipmentsDeliveryLink({ receipt, confirmedAt, onOpenDelivery }: Props) {
  if (receipt?.method === "PICKUP") {
    return (
      <NoticeStrip>
        <span className="flex flex-wrap items-center gap-1.5">
          <MapPin className="size-4 shrink-0" aria-hidden />
          Самовывоз: отгрузка перевозчиком не нужна — клиент заберёт заказ в
          пункте магазина.
        </span>
      </NoticeStrip>
    );
  }

  if (!receipt || !confirmedAt) {
    return (
      <NoticeStrip
        tone="warning"
        action={
          <Button
            variant="outline"
            size="sm"
            className="min-h-11"
            onClick={onOpenDelivery}
          >
            Перейти к доставке
          </Button>
        }
      >
        Условия ещё не согласованы — отгрузку лучше создавать после того, как
        клиент подтвердит способ и стоимость.
      </NoticeStrip>
    );
  }

  return (
    <NoticeStrip
      action={
        <Button variant="outline" size="sm" className="min-h-11" onClick={onOpenDelivery}>
          Открыть «Доставку»
          <ArrowRight className="ml-1 size-4" aria-hidden />
        </Button>
      }
    >
      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <Truck className="size-4 shrink-0" aria-hidden />
        <span className="font-medium">
          Доставка: {receipt.carrierName ?? receipt.label}
        </span>
        <span className="text-on-surface-variant">· {destinationText(receipt)}</span>
        <span className="text-on-surface-variant">· {priceText(receipt)}</span>
        <span className="text-on-surface-variant">
          · согласовано {formatShopDateTime(confirmedAt)}
        </span>
      </span>
    </NoticeStrip>
  );
}

/**
 * «ПВЗ CHE217» или адрес одной строкой.
 *
 * Код ПВЗ берём из снапшота условий, а не из расчёта: у заказа с витрины
 * расчёта может не быть вовсе, а пункт выдачи клиент уже выбрал.
 */
function destinationText(receipt: OrderDeliveryReceiptDto): string {
  const code = receipt.pickupPointCode ?? receipt.quote?.destination.pickupPointCode;
  if (code) return `ПВЗ ${code}`;
  return receipt.address?.trim() || "адрес не указан";
}

/** «450,00 ₽» / «бесплатно» / «сумма не выставлена». */
function priceText(receipt: OrderDeliveryReceiptDto): string {
  if (receipt.deliveryAmount === null) return "сумма не выставлена";
  const amount = Number(receipt.deliveryAmount);
  if (amount === 0) return "бесплатно";
  return `${formatMoney(receipt.deliveryAmount)} ₽`;
}
